import { Role } from '@infiltre/shared';
import { useEffect, useState } from 'react';
import { type MessageKey, useT } from '@/shared/lib/i18n';
import type { MugshotConfig } from '@/shared/lib/mugshot';
import { roleTint } from '@/shared/lib/role';
import { Button, HandKicker, MugshotPortrait, PaperSheet, Pin, RedactedBlock } from '@/shared/ui';
import { DistributionHeader } from './DistributionHeader';

export type DossierRevealProps = {
  playerName: string;
  playerOrder?: number;
  playerId?: string;
  playerMugshot: MugshotConfig;
  role: Role;
  word: string;
  onAcknowledge: () => void;
  /** One-shot when redactions first lift. */
  onReveal?: () => void;
  /** Online mode: auto-lift after delay (no input needed). */
  autoRevealMs?: number;
  acknowledgeLabel?: string;
  /** When provided, the distribution header (Phase 01 + TOUR + dots) is rendered on top. */
  step?: number;
  total?: number;
};

/**
 * Press-and-hold dossier card. Pinned cream phase header on top, then a
 * cream paper sheet with the player's polaroid mugshot, name field,
 * permanently-redacted ROLE field, and a MOT-CLÉ slab that lifts while
 * pressed. The Clochard is the single role exception: their RÔLE
 * unmasks on press (so they know to bluff) and the word section is
 * replaced by a "no word" hint.
 */
export const DossierReveal = ({
  playerName,
  playerOrder,
  playerId,
  playerMugshot,
  role,
  word,
  onAcknowledge,
  onReveal,
  autoRevealMs,
  acknowledgeLabel,
  step,
  total,
}: DossierRevealProps) => {
  const t = useT();
  const [pressing, setPressing] = useState(false);
  const [seen, setSeen] = useState(false);

  // Online auto-reveal — fires once after `autoRevealMs` and stays open.
  useEffect(() => {
    if (autoRevealMs === undefined) return;
    const id = setTimeout(() => {
      setPressing(true);
      setSeen(true);
      onReveal?.();
    }, autoRevealMs);
    return () => clearTimeout(id);
  }, [autoRevealMs, onReveal]);

  const startPress = (e: React.MouseEvent | React.TouchEvent): void => {
    e.preventDefault();
    if (!seen) onReveal?.();
    setPressing(true);
    setSeen(true);
  };
  const endPress = (): void => {
    if (autoRevealMs !== undefined) return;
    setPressing(false);
  };

  const fileNumber = String((playerOrder ?? 0) + 1).padStart(3, '0');
  const isClochard = role === Role.Clochard;
  const tint = roleTint(role);
  const roleLabel = t(`role.${role}` as MessageKey);

  return (
    <main className="flex h-full w-full flex-col items-stretch gap-4 px-4 pt-4 pb-6 sm:px-8">
      {step !== undefined && total !== undefined && (
        <DistributionHeader step={step} total={total} />
      )}

      <div className="flex flex-1 items-center justify-center">
        <button
          type="button"
          onMouseDown={startPress}
          onMouseUp={endPress}
          onMouseLeave={endPress}
          onTouchStart={startPress}
          onTouchEnd={endPress}
          onTouchCancel={endPress}
          className="relative w-full max-w-md cursor-pointer text-left transition-transform"
          style={{
            transform: `rotate(-1deg) ${pressing ? 'scale(.99)' : 'scale(1)'}`,
            userSelect: 'none',
            WebkitUserSelect: 'none',
          }}
        >
          <div className="paper-dossier relative px-5 pt-5 pb-6">
            <Pin left={20} top={-4} />
            <Pin left={'calc(100% - 20px)'} top={-4} />

            <div className="flex justify-between font-type text-[10px] text-ink/55 tracking-[0.22em]">
              <span>FILE · {fileNumber}</span>
              <span style={{ color: 'var(--color-stamp-red)' }}>EYES ONLY</span>
            </div>

            <div className="mt-4 flex items-start gap-4">
              <div className="relative w-[110px] flex-shrink-0">
                <Pin left={'50%'} top={-3} />
                <PaperSheet
                  seed={`mug:${playerId ?? playerName}`}
                  className="px-1 pt-1 pb-1.5"
                  style={{ boxShadow: 'var(--shadow-paper-soft)' }}
                >
                  <div className="relative flex aspect-[3/4] items-center justify-center overflow-hidden border border-ink/15">
                    <MugshotPortrait config={playerMugshot} className="scale-150" />
                    <span className="absolute top-1 left-1.5 font-mono text-[8px] text-ink/55 tracking-[0.2em]">
                      #{fileNumber}
                    </span>
                    <span className="absolute right-1.5 bottom-1 font-mono text-[8px] text-ink/55 tracking-[0.18em]">
                      F-{String((((playerOrder ?? 0) + 1) * 73) % 1000).padStart(3, '0')}
                    </span>
                  </div>
                  <div className="mt-0.5 truncate text-center font-hand text-[16px] text-ink leading-tight">
                    {playerName}
                  </div>
                </PaperSheet>
              </div>

              <div className="min-w-0 flex-1 space-y-3">
                <div>
                  <div className="type text-[9px] text-ink/55 uppercase tracking-[0.22em]">
                    {t('round.dossier.field_name')}
                  </div>
                  <div className="hand mt-0.5 break-words text-[24px] text-ink capitalize leading-[1.05]">
                    {playerName}
                  </div>
                </div>
                <div>
                  <div className="type text-[9px] text-ink/55 uppercase tracking-[0.22em]">
                    {t('round.dossier.field_role')}
                  </div>
                  <div className="mt-1">
                    {/*
                     * Role visibility is single-exception: only Clochard
                     * sees their role text on press (so they know to
                     * bluff). Everyone else stays redacted forever — the
                     * deduction is the entire game.
                     */}
                    <RedactedBlock reveal={isClochard && pressing} h={36}>
                      <div
                        className="break-words font-display font-extrabold text-[24px] uppercase leading-none tracking-[0.04em]"
                        style={{ color: tint }}
                      >
                        {isClochard ? roleLabel : '???'}
                      </div>
                    </RedactedBlock>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-5 border-ink/20 border-t border-dashed pt-3">
              {isClochard ? (
                <div>
                  <div className="type text-[9px] text-ink/55 uppercase tracking-[0.22em]">
                    {t('round.dossier.field_word')}
                  </div>
                  <div className="hand mt-1 text-[20px] text-ink/55 italic">
                    {t('round.no_word')}
                  </div>
                  {pressing && (
                    <HandKicker size={14} color="var(--color-magenta)" rot={-1} className="mt-2">
                      {t('round.no_word_hint')}
                    </HandKicker>
                  )}
                </div>
              ) : (
                <>
                  <div className="type text-[9px] text-ink/55 uppercase tracking-[0.22em]">
                    {t('round.dossier.field_word')}
                  </div>
                  <div className="mt-1">
                    <RedactedBlock reveal={pressing} h={56}>
                      <div className="break-words font-display font-extrabold text-[44px] text-ink uppercase leading-none">
                        {word}
                      </div>
                    </RedactedBlock>
                  </div>
                </>
              )}
            </div>

            {!pressing && !seen && (
              <HandKicker
                size={16}
                color="var(--color-magenta)"
                rot={-1.5}
                className="mt-4 block text-center"
              >
                ↓ {t('play.privacy_gate.hold_to_reveal')} ↓
              </HandKicker>
            )}
          </div>
        </button>
      </div>

      <div className="mx-auto w-full max-w-md flex-shrink-0">
        <div className="hand mb-3 text-center text-[16px] text-paper [text-shadow:0_1px_3px_rgba(0,0,0,.6)]">
          {autoRevealMs !== undefined
            ? ''
            : pressing
              ? t('round.dossier.release_to_hide')
              : `— ${t('play.privacy_gate.hold_to_reveal')}`}
        </div>
        <Button
          variant="orange"
          onClick={onAcknowledge}
          disabled={!seen || (pressing && autoRevealMs === undefined)}
        >
          {acknowledgeLabel ?? `${t('round.dossier.close_cta')} →`}
        </Button>
      </div>
    </main>
  );
};
